import { useCallback, useContext, useEffect, useRef, useSyncExternalStore } from 'react'
import {
  GlobalDataContext,
  TemplateContext,
  evaluateTemplateValue,
  useStore,
} from '@ea-lab/reactive-json'

/**
 * BrowserStorageSync — miroir « clé du store ↔ entrée du stockage local du navigateur ».
 *
 * POURQUOI DU JS (et rien de plus). Retenir un choix du visiteur d'une page à
 * l'autre SANS compte (sélection à comparer, listes sauvegardées, favoris) passe
 * par `localStorage`, une API du navigateur qu'aucun rjBuild n'exprime ; le cœur
 * de reactive-json n'a aucune réaction de stockage. Ce composant ne rend RIEN et
 * ne connaît aucune donnée métier : il recopie une clé du store dans une entrée
 * de stockage, et inversement. Tout le reste — cocher, retirer, nommer une liste —
 * reste du YAML (`toggleData`, `addData`, `removeData`, `setData`).
 *
 * Sens de circulation :
 *   - au montage : stockage → store (si l'entrée existe et a la FORME de la valeur
 *     déclarée : tableau pour un tableau, objet pour un objet) ;
 *   - ensuite : store → stockage à chaque changement de la clé ;
 *   - un autre onglet qui modifie l'entrée (`storage`) met ce store à jour, et un autre
 *     BLOC de la même page aussi : chaque bloc a son propre store, et `storage` ne se
 *     déclenche pas dans l'onglet qui écrit — d'où l'événement `orbit-storage` émis après
 *     chaque écriture (sans lui, une case « Comparer » d'un bloc écrasait la sélection
 *     faite dans un autre bloc de la page).
 * `urlParam` (facultatif) : si l'adresse porte ce paramètre (répété pour une
 * liste, `?ids=a&ids=b`), SES valeurs priment sur le stockage — un lien partagé
 * ouvre la sélection de celui qui l'a envoyé — et sont écrites dans le stockage.
 *
 * Le stockage peut être indisponible (navigation privée, sites bloqués) : chaque
 * accès est protégé, et le bloc fonctionne alors le temps de la page.
 *
 * Usage YAML :
 *   data:
 *     compare: []                 # la forme déclarée fait foi
 *   renderView:
 *     - type: BrowserStorageSync
 *       key: compare              # clé à la RACINE du store du bloc
 *       storageKey: dbr-compare   # nom de l'entrée de stockage (partagée entre blocs)
 *       urlParam: ids             # facultatif
 */

const read = (storageKey) => {
  try {
    const raw = window.localStorage.getItem(storageKey)
    return raw == null ? undefined : JSON.parse(raw)
  } catch {
    return undefined
  }
}

// Événement des AUTRES blocs de la page (`storage` ne vise que les autres onglets).
const SAME_TAB_EVENT = 'orbit-storage'

const write = (storageKey, value) => {
  const json = JSON.stringify(value ?? null)
  try {
    if (window.localStorage.getItem(storageKey) === json) return
    window.localStorage.setItem(storageKey, json)
  } catch {
    // Stockage plein ou interdit : la valeur vit le temps de la page.
    return
  }
  window.dispatchEvent(new CustomEvent(SAME_TAB_EVENT, { detail: storageKey }))
}

// La valeur lue a-t-elle la forme de la valeur déclarée ? (un tableau reste un tableau)
const sameShape = (declared, value) => {
  if (value === undefined || value === null) return false
  if (Array.isArray(declared)) return Array.isArray(value)
  if (declared && typeof declared === 'object') return typeof value === 'object' && !Array.isArray(value)
  return typeof value === typeof declared
}

export const BrowserStorageSync = ({ props }) => {
  const globalDataContext = useContext(GlobalDataContext)
  const templateContext = useContext(TemplateContext)
  const store = useStore()
  const resolve = (v) => evaluateTemplateValue({ valueToEvaluate: v, globalDataContext, templateContext })

  const key = resolve(props.key)
  const storageKey = resolve(props.storageKey)
  const urlParam = resolve(props.urlParam)
  const ready = useRef(false)
  // Instantané d'AVANT l'hydratation : tant que l'instantané vaut encore cela, la valeur
  // relue n'est pas arrivée dans le store, et l'écrire effacerait le stockage (puis, par
  // l'événement du même onglet, la valeur des autres blocs). Au premier changement — la
  // valeur relue, éventuellement fusionnée avec un geste du visiteur —, l'attente cesse.
  const pending = useRef(null)
  // Contexte COURANT pour l'écouteur posé au montage (il vit plus longtemps qu'un rendu).
  const context = useRef(globalDataContext)
  context.current = globalDataContext

  // Abonnement RÉACTIF à la clé (un plugin lit sinon le store une fois pour toutes) ;
  // l'instantané est une chaîne, comparée par valeur.
  const subscribe = useCallback((onChange) => (key ? store.subscribe(key, onChange) : () => {}), [store, key])
  const getSnapshot = useCallback(() => JSON.stringify(key ? store.get(key) ?? null : null), [store, key])
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot)

  // Montage : l'adresse, sinon le stockage, alimente le store.
  useEffect(() => {
    if (!key || !storageKey || typeof window === 'undefined') return
    const declared = store.get(key)
    let initial
    if (urlParam) {
      const params = new URLSearchParams(window.location.search)
      if (params.has(urlParam)) {
        const values = params.getAll(urlParam).filter((v) => v !== '')
        // Un paramètre vide (`?ids=`) ne doit pas effacer la valeur gardée.
        if (values.length) initial = Array.isArray(declared) ? values : values[0]
      }
    }
    if (initial === undefined) {
      const stored = read(storageKey)
      if (sameShape(declared, stored)) initial = stored
    }
    if (initial !== undefined && JSON.stringify(initial) !== JSON.stringify(declared)) {
      pending.current = JSON.stringify(declared ?? null)
      globalDataContext.updateData(initial, `data.${key}`)
    }
    if (initial !== undefined) write(storageKey, initial)
    ready.current = true

    const onStorage = (event) => {
      if ((event.type === SAME_TAB_EVENT ? event.detail : event.key) !== storageKey) return
      const value = read(storageKey)
      if (sameShape(store.get(key), value) && JSON.stringify(value) !== JSON.stringify(store.get(key))) {
        context.current.updateData(value, `data.${key}`)
      }
    }
    window.addEventListener('storage', onStorage)
    window.addEventListener(SAME_TAB_EVENT, onStorage)
    return () => {
      window.removeEventListener('storage', onStorage)
      window.removeEventListener(SAME_TAB_EVENT, onStorage)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, storageKey, urlParam])

  // Ensuite : chaque changement de la clé est écrit dans le stockage.
  useEffect(() => {
    if (!ready.current || !storageKey || typeof window === 'undefined') return
    if (pending.current !== null) {
      if (snapshot === pending.current) return
      pending.current = null
    }
    write(storageKey, JSON.parse(snapshot))
  }, [snapshot, storageKey])

  return null
}
